import React, { FunctionComponent, useState } from "react";
import { getValidationAttributes } from "../../functions/get-validation-attributes";
import { addClass } from "../../functions/string";
import { Guid } from "../../util/Guid";

/**
 * Mobile number input properties. Used to configure mobile number input control.
 */
export type MobileNumberProps = { options: MobileNumberOptions };

/**
 * Mobile number input configuration options.
 */
export type MobileNumberOptions = {

	/**
	 * Initial value for the mobile number input.
	 */
	value?: string;

	/**
	 * Initially selected country code.
	 */
	selectedCountry?: string;

	/**
	 * On change handler, called when the input changes.
	 */
	onChange?: (number: string) => void;

	/**
	 * Clear handler, used by caller to clear user input.
	 */
	clear?: () => void;

	/**
	 * Placeholder text displayed when there is no input value.
	 */
	placeholder: string;

	/**
	 * Target input element.
	 */
	input: HTMLInputElement;
};

/**
 * Mobile number input state.
 */
interface MobileNumberInputState {
	value: string;
	country: TelCountry;
	originalValue: string;
	guid: string;
	isInputInvalid: boolean;
}

/**
 * Telephone country interface, used to configure country selection options.
 */
interface TelCountry {
	code: string;
	name: string;
	className: string;
	prefix: string;
}

// GB
const gb: TelCountry = { code: "gb", name: "GB", className: "tel-co-gb", prefix: "07" };
// Ireland
const ie: TelCountry = { code: "ie", name: "IE", className: "tel-co-ie", prefix: "08" };

/**
 * Clean mobile number function.
 * @param number The number to clean.
 * @returns Cleaned mobile number. 
 */
const clean = (number: string) => {

	if (number.match(/([^0-9])/)) {
		number = number.replace(/([^0-9])/, "")
	}

	if (number.startsWith("447")) {
		number = number.replace("447", gb.prefix);
	}
	else if (number.startsWith("44")) {
		number = number.replace("44", "0");
	}
	else if (number.startsWith("3537")) {
		number = number.replace("3537", "0");
	}
	else if (number.startsWith("353")) {
		number = number.replace("353", ie.prefix);
	}

	return number;
}

/**
 * Mobile number input compoment. Allows country code selection and constrains input.
 * @param props Mobile number input properties, enabling configuration and event handling.
 */
export const MobileNumberInput: FunctionComponent<MobileNumberProps> = function (props: MobileNumberProps) {

	// setup initial state
	const [state, setState] = useState({
		originalValue: props.options.value ? props.options.value : gb.prefix,
		value: props.options.value ? props.options.value : gb.prefix,
		country: props.options.value?.startsWith(ie.prefix) ? ie : gb,
		guid: Guid.newGuid()
	} as MobileNumberInputState);

	// refresh state handler
	const refreshState = () => {
		setState({ ...state });
	};

	// reset validation handler
	const resetValidation = () => {
		state.isInputInvalid = false;
	}

	// change handler
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

		let number = e.target.value;

		// if number has changed, update value and country state
		if (number !== state.value) {

			state.value = number;
			state.country = state.value?.startsWith(ie.prefix) ? { ...ie } : { ...gb };
		}

		refreshState();
	};

	// blur handler
	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {

		// clear existing validation
		resetValidation();

		// set cleaned value and country state
		state.value = clean(e.target.value);
		state.country = state.value.startsWith(ie.prefix) ? { ...ie } : { ...gb };

		if ((!state.value || state.value === gb.prefix || state.value === ie.prefix) && props.options.input.hasAttribute("data-val-required")) {
			state.isInputInvalid = true;
		}
		// validate the input
		else if (state.value && props.options.input.hasAttribute("data-val-regex")) {

			const pattern = props.options.input.getAttribute("data-val-regex-pattern");
			if (pattern) {
				const formatRegex = new RegExp(pattern);
				if (!formatRegex.test(state.value)) {
					state.isInputInvalid = true;
				}
			}
		}

		// call provided on change handler
		if (state.value !== state.originalValue && props.options.onChange) {

			props.options.onChange(state.value);
		}

		// stash original value for comparison
		state.originalValue = state.value;

		refreshState();
	};

	// country change handler
	const handleCountryChange = () => {

		let newPrefix: string;
		if (state.country.code == gb.code) {
			state.country = { ...ie };
			newPrefix = ie.prefix;
		}
		else {
			state.country = { ...gb };
			newPrefix = gb.prefix;
		}

		state.value = state.value.length > 2 ? newPrefix + state.value.substring(2) : newPrefix;

		if (props.options.onChange) {
			props.options.onChange(state.value);
		}

		refreshState();
	};

	const defaultCssClass = "form-control mobile-input";
	const cssClass = state.isInputInvalid ? addClass(defaultCssClass, "input-validation-error") : defaultCssClass;

	// output the component
	return (
		<React.Fragment>
			<button
				className={"btn phone-input-country " + state.country.className}
				type="button"
				onClick={handleCountryChange}
				title="Change mobile number country">{state.country.name}</button>
			<input
				name={state.guid}
				type="tel"
				className={cssClass}
				value={state.value}
				onChange={handleChange}
				onBlur={handleBlur}
				{...getValidationAttributes(props.options.input)}
			/>
		</React.Fragment>
	);
}